import { JsTimeout } from '@/lib/helpers';
import { FC, ReactNode, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { animateScroll } from 'react-scroll';
import { debounce } from 'lodash';
import { ScrollContext } from './helpers';

interface Props {
  children: ReactNode;
  scrollRef: RefObject<HTMLDivElement>;
}

export const ScrollProvider: FC<Props> = ({ children, scrollRef }) => {
  const tailIntervalRef = useRef<JsTimeout>();
  const tailStartRef = useRef<JsTimeout>();
  const [atBottom, setAtBottom] = useState(true);
  const lastScrollTop = useRef(0);

  const scrollToBottom = useCallback(
    (opts?: Record<string, number | string | boolean>) => {
      animateScroll.scrollToBottom({
        duration: 130,
        smooth: true,
        delay: 0,
        containerId: scrollRef.current?.id,
        ...(opts || {}),
      });
    },
    [scrollRef]
  );

  const jumpToBottom = useCallback(() => {
    scrollToBottom({
      duration: 0,
      smooth: false,
    });
  }, [scrollToBottom]);

  const stopTail = useCallback(() => {
    if (tailStartRef.current) {
      clearTimeout(tailStartRef.current);
      tailStartRef.current = undefined;
    }

    if (tailIntervalRef.current) {
      clearInterval(tailIntervalRef.current);
      tailIntervalRef.current = undefined;
    }
  }, []);

  const startTail = useCallback(() => {
    stopTail();

    scrollToBottom({ delay: 10 });

    tailStartRef.current = setTimeout(() => {
      tailIntervalRef.current = setInterval(() => {
        scrollToBottom({
          duration: 10,
        });
      }, 100);
    }, 1500);
  }, [stopTail, scrollToBottom]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleScrollWatchBottom = useCallback(
    debounce((event: UIEvent) => {
      const scrollEl = event.target as HTMLDivElement;
      const scrollOffset = scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight;
      setAtBottom(scrollOffset <= 1);
    }, 50),
    [stopTail]
  );

  // if there's an upward scroll while tailing, stop tailing
  const handleScrollWatchTail = useCallback(
    (event: UIEvent) => {
      const scrollEl = event.target as HTMLDivElement;

      if (tailIntervalRef.current && scrollEl.scrollTop < lastScrollTop.current) {
        stopTail();
      }

      lastScrollTop.current = scrollEl.scrollTop;
    },
    [stopTail]
  );

  useEffect(() => {
    if (!scrollRef.current) {
      return;
    }

    const scrollElement = scrollRef.current;
    scrollElement.addEventListener('scroll', handleScrollWatchBottom);
    scrollElement.addEventListener('scroll', handleScrollWatchTail);

    return () => {
      scrollElement.removeEventListener('scroll', handleScrollWatchBottom);
      scrollElement.removeEventListener('scroll', handleScrollWatchTail);
    };
  }, [scrollRef, handleScrollWatchBottom, handleScrollWatchTail]);

  return (
    <ScrollContext.Provider
      value={{
        scrollRef,
        jumpToBottom,
        scrollToBottom,
        startTail,
        stopTail,
        atBottom,
        tailing: !!tailIntervalRef.current,
      }}
    >
      {children}
    </ScrollContext.Provider>
  );
};
